import React, { useState } from 'react';
import { Heart, ThumbsUp, Laugh, Angry, Frown, Star, Smile } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { addDoc, collection, query, where, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/providers/AuthProvider';
import { cn } from '@/lib/utils';

interface MessageReactionPickerProps {
  messageId: string;
  className?: string;
}

// Use the same reactions as posts for consistency
const REACTIONS = [
  { type: 'like', icon: ThumbsUp, color: 'text-blue-500', bgColor: 'bg-blue-50', label: 'Like' },
  { type: 'love', icon: Heart, color: 'text-red-500', bgColor: 'bg-red-50', label: 'Love' },
  { type: 'haha', icon: Laugh, color: 'text-yellow-500', bgColor: 'bg-yellow-50', label: 'Haha' },
  { type: 'wow', icon: Star, color: 'text-green-500', bgColor: 'bg-green-50', label: 'Wow' },
  { type: 'sad', icon: Frown, color: 'text-purple-500', bgColor: 'bg-purple-50', label: 'Sad' },
  { type: 'angry', icon: Angry, color: 'text-orange-500', bgColor: 'bg-orange-50', label: 'Angry' },
];

export function MessageReactionPicker({ messageId, className }: MessageReactionPickerProps) {
  const [showPicker, setShowPicker] = useState(false);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const addReaction = async (type: string) => {
    if (!user || !messageId) return;

    setLoading(true);
    try {
      // Check if user already has a reaction on this message
      const reactionsRef = collection(db, 'messageReactions');
      const q = query(reactionsRef, 
        where('messageId', '==', messageId),
        where('userId', '==', user.uid)
      );
      
      const snapshot = await getDocs(q);
      
      // Remove existing reaction
      const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
      
      // Add new reaction (unless clicking the same one - toggle behavior)
      const existingType = snapshot.docs[0]?.data()?.type;
      if (existingType !== type) {
        await addDoc(collection(db, 'messageReactions'), {
          messageId,
          userId: user.uid,
          userName: user.displayName || 'Anonymous',
          type,
          createdAt: new Date()
        });
      }
      
      setShowPicker(false);
    } catch (error) {
      console.error('Error adding reaction:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DropdownMenu open={showPicker} onOpenChange={setShowPicker} modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0 rounded-full hover:bg-gray-200"
          title="React to message"
        >
          <Smile className="w-3.5 h-3.5 text-gray-500" />
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent 
        align="start" 
        side="top"
        className="flex p-2 gap-1 bg-white rounded-xl shadow-2xl border min-w-fit"
        style={{ 
          zIndex: 9999,
          position: 'fixed'
        }}
        avoidCollisions={true}
        collisionPadding={10}
      >
        {REACTIONS.map(({ type, icon: Icon, color, bgColor, label }) => (
          <Button
            key={type}
            variant="ghost"
            size="sm"
            disabled={loading}
            className={cn(
              'h-10 w-10 p-0 rounded-lg hover:bg-gray-100 transition-all transform hover:scale-110',
              'flex items-center justify-center'
            )}
            onClick={() => addReaction(type)}
            title={label}
          >
            <Icon 
              className={cn('w-5 h-5', color)} 
              fill="none"
              stroke="currentColor"
            />
          </Button>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
